import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FocusScope, useFocusManager } from '@react-aria/focus';
import { useEditor, useNode, useEditingCond } from '@splashthat/splash-cms-engine';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useSessionRegistration } from 'common/components/TouchpointEditor/hooks/useSessionRegistration';
import Spacer from 'common/components/Spacer';
import { SessionsFooterContainer } from 'common/components/SessionCard/SessionCard.styles';
import { useModal } from 'common/components/TouchpointEditor/contexts/ModalManager';

import SessionButtonWrapper from './SessionButtonWrapper';
import {
    NODE_DESCENDANT_KEY_ADD_TO_CALENDAR_BUTTON,
    NODE_DESCENDANT_KEY_REGISTER_SESSION_BUTTON,
    NODE_DESCENDANT_KEY_UNREGISTER_SESSION_BUTTON,
    NODE_DESCENDANT_KEY_REGISTER_WAITLIST_SESSION_BUTTON,
    NODE_DESCENDANT_KEY_UNREGISTER_WAITLIST_SESSION_BUTTON,
    NODE_ID_ADD_SESSION_TO_CALENDAR,
} from '../../constants';
import {
    ADD_TO_CALENDAR_BUTTON_ID,
    REGISTER_SESSION_BUTTON_ID,
    REGISTER_WAITLIST_SESSION_BUTTON_ID,
    UNREGISTER_SESSION_BUTTON_ID,
    UNREGISTER_WAITLIST_SESSION_BUTTON_ID,
    useSessionRegistrationElementsVisibility,
} from '../../../contexts/SessionRegistrationStateProvider';

function SessionButtonsFocusRestorer({ children, visibleElements }) {
    const focusManager = useFocusManager();
    const previousVisibleElementsRef = useRef(null);

    useEffect(() => {
        const visibleElementsKey = visibleElements?.join(',') || '';
        const hasChanged =
            previousVisibleElementsRef.current !== null &&
            visibleElementsKey &&
            previousVisibleElementsRef.current !== visibleElementsKey;

        const shouldRestore = !document.activeElement || document.activeElement === document.body;

        if (hasChanged && shouldRestore) {
            focusManager.focusFirst({ tabbable: true });
        }

        previousVisibleElementsRef.current = visibleElementsKey;
    }, [visibleElements, focusManager]);

    return <SessionsFooterContainer tabIndex={-1}>{children}</SessionsFooterContainer>;
}

SessionButtonsFocusRestorer.propTypes = {
    children: PropTypes.node.isRequired,
    visibleElements: PropTypes.array.isRequired,
};

const SESSION_ACTION = {
    REGISTER: 'Registered',
    UNREGISTER: 'Unregistered',
    WAITLISTED: 'Waitlisted',
};

export default function SessionButtonsWrapper({
    registerSessionButton,
    addToCalendarButton,
    unregisterSessionButton,
    registerWaitlistSessionButton,
    unregisterWaitlistSessionButton,
    disableContentEditing,
    session,
}) {
    const {
        theme: { colors, activeFonts },
        event,
    } = useTouchpointData();
    const editor = useEditor((state) => ({ editing: state.editing }));
    const node = useNode();
    const setContent = useCallback(
        (descendantKey) => (text) =>
            node.actions.setProp((editedNode) => {
                editedNode.descendants[descendantKey].meta.content = text; // eslint-disable-line no-param-reassign
                return editedNode;
            }),
        [],
    );

    const sessionRegistration = useSessionRegistration();
    const modal = useModal();
    const onAddToCalendarClickIfEditing = () => editor.actions.selectNode(NODE_DESCENDANT_KEY_ADD_TO_CALENDAR_BUTTON);
    const onAddToCalendarClickIfNotEditing = useCallback(
        () => modal.open(NODE_ID_ADD_SESSION_TO_CALENDAR, { session, eventId: event.id }),
        [],
    );

    const onAddToCalendarClick = useEditingCond(onAddToCalendarClickIfEditing, onAddToCalendarClickIfNotEditing);

    const visibleElements = useSessionRegistrationElementsVisibility({ session, event, editor });

    const getButtonAriaLabel = (meta) => {
        const buttonContent = meta?.content;

        if (!buttonContent) {
            return '';
        }
        return session.name ? `${buttonContent} - ${session.name}` : buttonContent;
    };

    const registerSessionButtonProps = {
        styles: registerSessionButton?.styles,
        meta: registerSessionButton?.meta,
        setContent: setContent(NODE_DESCENDANT_KEY_REGISTER_SESSION_BUTTON),
        ariaLabel: getButtonAriaLabel(registerSessionButton?.meta),
    };

    const unregisterSessionButtonProps = {
        styles: unregisterSessionButton?.styles,
        meta: unregisterSessionButton?.meta,
        setContent: setContent(NODE_DESCENDANT_KEY_UNREGISTER_SESSION_BUTTON),
        ariaLabel: getButtonAriaLabel(unregisterSessionButton?.meta),
    };

    const registerWaitlistSessionButtonProps = {
        styles: registerWaitlistSessionButton?.styles,
        meta: registerWaitlistSessionButton?.meta,
        setContent: setContent(NODE_DESCENDANT_KEY_REGISTER_WAITLIST_SESSION_BUTTON),
        ariaLabel: getButtonAriaLabel(registerWaitlistSessionButton?.meta),
    };

    const unregisterWaitlistSessionButtonProps = {
        styles: unregisterWaitlistSessionButton?.styles,
        meta: unregisterWaitlistSessionButton?.meta,
        setContent: setContent(NODE_DESCENDANT_KEY_UNREGISTER_WAITLIST_SESSION_BUTTON),
        ariaLabel: getButtonAriaLabel(unregisterWaitlistSessionButton?.meta),
    };

    const addToCalendarButtonProps = {
        styles: addToCalendarButton?.styles,
        meta: addToCalendarButton?.meta,
        setContent: setContent(NODE_DESCENDANT_KEY_ADD_TO_CALENDAR_BUTTON),
        ariaLabel: getButtonAriaLabel(addToCalendarButton?.meta),
    };

    if (!visibleElements?.length) {
        return null;
    }

    return (
        <>
            <Spacer $spacing="1.5rem" />
            <FocusScope>
                <SessionButtonsFocusRestorer visibleElements={visibleElements}>
                    {visibleElements.includes(REGISTER_SESSION_BUTTON_ID) ? (
                        <SessionButtonWrapper
                            {...registerSessionButtonProps}
                            colors={colors}
                            activeFonts={activeFonts}
                            disableContentEditing={disableContentEditing}
                            onClick={node.actions.onClick(() =>
                                sessionRegistration.actions.registerForSession(session.id),
                            )}
                            loading={sessionRegistration.isRegistering}
                            success={sessionRegistration.isRegisteringSuccess}
                            successMessage={SESSION_ACTION.REGISTER}
                        />
                    ) : null}
                    {visibleElements.includes(UNREGISTER_SESSION_BUTTON_ID) ? (
                        <SessionButtonWrapper
                            {...unregisterSessionButtonProps}
                            colors={colors}
                            activeFonts={activeFonts}
                            disableContentEditing={disableContentEditing}
                            onClick={node.actions.onClick(() =>
                                sessionRegistration.actions.unregisterForSession(session.id),
                            )}
                            loading={sessionRegistration.isUnregistering}
                            success={sessionRegistration.isUnregisteringSuccess}
                            successMessage={SESSION_ACTION.UNREGISTER}
                        />
                    ) : null}
                    {visibleElements.includes(REGISTER_WAITLIST_SESSION_BUTTON_ID) ? (
                        <SessionButtonWrapper
                            {...registerWaitlistSessionButtonProps}
                            colors={colors}
                            activeFonts={activeFonts}
                            disableContentEditing={disableContentEditing}
                            onClick={node.actions.onClick(() =>
                                sessionRegistration.actions.registerForSession(session.id),
                            )}
                            loading={sessionRegistration.isRegistering}
                            success={sessionRegistration.isRegisteringSuccess}
                            successMessage={SESSION_ACTION.WAITLISTED}
                        />
                    ) : null}
                    {visibleElements.includes(UNREGISTER_WAITLIST_SESSION_BUTTON_ID) ? (
                        <SessionButtonWrapper
                            {...unregisterWaitlistSessionButtonProps}
                            colors={colors}
                            activeFonts={activeFonts}
                            disableContentEditing={disableContentEditing}
                            onClick={node.actions.onClick(() =>
                                sessionRegistration.actions.unregisterForSession(session.id),
                            )}
                            loading={sessionRegistration.isUnregistering}
                            success={sessionRegistration.isUnregisteringSuccess}
                            successMessage={SESSION_ACTION.UNREGISTER}
                        />
                    ) : null}
                    {visibleElements.includes(ADD_TO_CALENDAR_BUTTON_ID) ? (
                        <SessionButtonWrapper
                            {...addToCalendarButtonProps}
                            colors={colors}
                            activeFonts={activeFonts}
                            disableContentEditing={disableContentEditing}
                            onClick={node.actions.onClick(onAddToCalendarClick)}
                            loading={false}
                            success={false}
                        />
                    ) : null}
                </SessionButtonsFocusRestorer>
            </FocusScope>
        </>
    );
}

SessionButtonsWrapper.propTypes = {
    registerSessionButton: PropTypes.object.isRequired,
    registerWaitlistSessionButton: PropTypes.object.isRequired,
    addToCalendarButton: PropTypes.object.isRequired,
    unregisterSessionButton: PropTypes.object.isRequired,
    unregisterWaitlistSessionButton: PropTypes.object.isRequired,
    disableContentEditing: PropTypes.bool.isRequired,
    session: PropTypes.object.isRequired,
};
